import { Container, Paper, Stack } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { PluginsForm } from '../plugins-form'
import { useTranslation } from 'react-i18next'
import { SectionHeader } from '@sitoo/mui-components'
import { useMutation, useQuery } from '@apollo/client'
import {
  GetLocalProductSearchDocument,
  PluginProductSearch,
  PluginProductSearchFiltersInnerTypeEnum,
  UpdateLocalProductSearchDocument,
} from '../../../../generated/graphql'
import { PredefinedValuesInput } from '../../../../inputs/predefined-values-input'

type Form = Pick<PluginProductSearch, 'filters'>

const systemFilters = [
  {
    id: 'system_brands',
    type: PluginProductSearchFiltersInnerTypeEnum.SingleEnum,
  },
  {
    id: 'system_categories',
    type: PluginProductSearchFiltersInnerTypeEnum.SingleEnum,
  },

  { id: 'system_in_stock', type: PluginProductSearchFiltersInnerTypeEnum.Bool },
  {
    id: 'system_in_campaign',
    type: PluginProductSearchFiltersInnerTypeEnum.Bool,
  },
]

export const LocalSettingsPosProductSearchPage = () => {
  const { t } = useTranslation(['apps', 'shared'])

  const formContext = useForm<Form>({
    defaultValues: {
      filters: [],
    },
  })

  const { control } = formContext

  const { data, loading: isLoading } = useQuery(GetLocalProductSearchDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      includeOnlyFilterable: true,
    },
  })

  const [updateSettings, { loading: isUpdating }] = useMutation(
    UpdateLocalProductSearchDocument,
  )

  const onSubmit = async (formData: Form) => {
    const { data } = await updateSettings({
      variables: {
        data: formData,
      },
    })

    return data?.updateLocalProductSearch
  }

  const options = [
    ...systemFilters.map((value) => ({
      value: value.id,
      label: t(`apps:pos_product_search.${value.id}`),
    })),
    ...(Array.isArray(data?.allCustomAttributes)
      ? data.allCustomAttributes.map((value) => ({
          value: value.id,
          label: value.title,
        }))
      : []),
  ]

  const getFilterFromId = (id: Form['filters'][number]['id']) => {
    const systemFilter = systemFilters.find((filter) => filter.id === id)
    if (systemFilter) {
      return {
        id: systemFilter.id,
        type: systemFilter.type,
      }
    }

    const filter = data?.allCustomAttributes.find((filter) => filter.id === id)
    return {
      id: filter?.id || '',
      type: PluginProductSearchFiltersInnerTypeEnum.SingleEnum,
    }
  }

  return (
    <Container>
      <FormProvider {...formContext}>
        <PluginsForm
          serverData={data?.localProductSearch}
          onSubmit={onSubmit}
          submitLabel={t('shared:action.save')}
          isLoading={isLoading || isUpdating}
        >
          <Paper sx={{ mb: 2 }}>
            <SectionHeader
              variant="transparent"
              description={t(
                'apps:pos_product_search.local_settings_description',
              )}
            >
              {t('apps:pos_product_search.filters_label')}
            </SectionHeader>

            <Stack gap={2} sx={{ pt: 1 }}>
              <PredefinedValuesInput
                control={control}
                name="filters"
                options={options}
                transform={{
                  input: (values) => values?.map((value) => value.id) || [],
                  output: (values) =>
                    values.map((value) => getFilterFromId(value)),
                }}
                addLabel={t('apps:pos_product_search.add_filter')}
              />
            </Stack>
          </Paper>
        </PluginsForm>
      </FormProvider>
    </Container>
  )
}
